<template>
  <div class="station-main-panel">
    <div class="station-leftbar" v-if="!stationIsBreakout">
      <div class="station-leftbar-graph">
        <StationPerformanceGraph ref="performanceChart" :productionStation="productionStation" v-if="productionStation.showStationGraph" />
      </div>

      <StationButtonGroup>
        <StationButtonGroupButton class="col-3" :active="scheduleTabIndex == 0" @click="scheduleTabIndex = 0">
          SHEETS
        </StationButtonGroupButton>
        <StationButtonGroupButton class="col-3" :active="scheduleTabIndex == 1" @click="scheduleTabIndex = 1">
          ITEMS
        </StationButtonGroupButton>
        <StationButtonGroupButton class="col-3" :active="scheduleTabIndex == 2" @click="scheduleTabIndex = 2">
          FULL SCHEDULE
        </StationButtonGroupButton>
        <StationButtonGroupButton class="col-3" :active="scheduleTabIndex == 3" @click="scheduleTabIndex = 3">
          ORDERS
        </StationButtonGroupButton>
      </StationButtonGroup>
      
      <div class="station-leftbar-list station-leftbar-list-with-button-group" ref="scheduleLeftBar">
        <OptimiserSheetList
          :stationId="productionStation.id"
          v-show="scheduleTabIndex == 0"
          @sheetClick="sheetClicked"
        />

        <OptimiserPanelList
          ref="optimiserPanelList"
          :stationId="productionStation.id"
          :selectedSheet="selectedSheet"
          @panelClick="panelClicked"
          @addPanelClick="addPanelsFromScheduleList"
          v-show="scheduleTabIndex == 1">
        </OptimiserPanelList>

        <StationPanelScheduleList 
          :stationId="productionStation.id"
          @panelClick="panelClicked"
          @urgentCountUpdated="$emit('urgentCountUpdated', $event)"
          v-show="scheduleTabIndex == 2">
        </StationPanelScheduleList>

        <StationOrderScheduleList 
          :stationId="productionStation.id"
          @orderClick="orderClicked"
          v-show="scheduleTabIndex == 3">
        </StationOrderScheduleList>
      </div>
    </div>
    <div class="station-mainarea">
      <div class="station-tool-window">
        <div class="station-view">

          <div class="station-optimisation-view">
            <StationButtonGroup>
              <StationButtonGroupButton class="col-2" :badge="stationSheetCount" badgeClass="badge-success" :active="cuttingTabIndex == 0" @click="selectOptimiserTab">
                OPTIMISER
              </StationButtonGroupButton>
              <StationButtonGroupButton class="col-2" :badge="statusWarningCount" badgeClass="badge-danger" :active="cuttingTabIndex == 1" @click="selectOptimiserStatusTab">
                STATUS
              </StationButtonGroupButton>
              <StationButtonGroupButton class="col-2" :active="cuttingTabIndex == 2" @click="selectManualCuttingTab">
                MANUAL CUTTING
              </StationButtonGroupButton>
              <StationButtonGroupButton class="col-3" :active="cuttingTabIndex == 3" @click="selectGlassSheetTab">
                MANUAL SHEET CUTTING
              </StationButtonGroupButton>
              <StationButtonGroupButton class="col-2" :active="cuttingTabIndex == 4" @click="selectManualCuttingLogTab">
                MANUAL CUT LOG
              </StationButtonGroupButton>
              <StationButtonGroupButton class="col-1" :active="cuttingTabIndex == 5" @click="selectBatchTab">
                BATCH
              </StationButtonGroupButton>
            </StationButtonGroup>
            
            <div v-show="cuttingTabIndex == 0" class="station-optimisation-tabpage station-optimiser-sheet-droppable-container">
              <div class="optimisation-top-container">

                <div class="optimisation-job-container">
                  <div class="station-optimisation-dropzone" v-show="showSheetDrop">Drop sheets here to include in job</div>

                  <div class="optimisation-job-list">

                    <QueueOptimiserSheet
                      v-for="sheet of stationSheetList"
                      :key="sheet.id"
                      :sheet="sheet"
                      :active="currentSheetId == sheet.id"
                      @click="selectSheet(sheet.id)" />

                    <div class="optimisation-job-list-loading" v-if="sheetListLoading">
                      <Spinner />
                    </div>

                    <QueueOptimiserOptions
                      v-if="!stationIsBreakout"
                      :queueOptionsAvailable="!stationSheetQueueEmpty"
                      @createSheetClicked="createSheet"
                      @optimiseQueueClicked="optimiseQueue"
                      @clearQueueClicked="clearQueue"
                      />
                  </div>
                </div>

                <div class="optimisation-editor-info" v-if="selectedSheet">
                  <div class="row">
                    <div class="col-12 font-weight-bold">{{selectedSheet.stock.name}}</div>
                  </div>
                  <div class="row">
                    <div class="col-3">Sheet ID</div><div class="col-9 font-weight-bold">{{selectedSheet.id}}</div>
                  </div>
                  <div class="row">
                    <div class="col-3">Size</div><div class="col-9 font-weight-bold">{{selectedSheet.height}} x {{selectedSheet.width}} 
                      <LabelBadge v-if="selectedSheet.isRotated" class="label-optimiser-sheet-rotated label-sm ml-2">ROTATED</LabelBadge>
                      <LabelBadge v-if="selectedSheet.glassLevel && selectedSheet.glassLevel.isOffcut" class="label-optimiser-sheet-offcut label-sm ml-2">OFFCUT</LabelBadge>
                      <LabelBadge v-if="selectedSheet.isCustomSize" class="label-optimiser-sheet-custom label-sm ml-2">CUSTOM</LabelBadge>
                    </div>
                  </div>
                  <div class="row">
                    <div class="col-3">Trim</div><div class="col-9 font-weight-bold">{{selectedSheet.trimTop}}mm&nbsp;&nbsp;{{selectedSheet.trimRight}}mm&nbsp;&nbsp;{{selectedSheet.trimBottom}}mm&nbsp;&nbsp;{{selectedSheet.trimLeft}}mm</div>
                  </div>
                  <div class="row" v-if="selectedSheet.stock.isStockUsingFaces">
                    <div class="col-3">Side Up</div><div class="col-9 font-weight-bold">{{formatLookupName(selectedSheet.stock.cuttingFaceSideUp, StockFaceTypeLookup)}}</div>
                  </div>
                  <div class="row" v-if="selectedSheet.stock.isStockUsingGrain">
                    <div class="col-3">Grain</div><div class="col-9 font-weight-bold">{{formatLookupName(selectedSheet.grainDirection, StockGrainDirectionTypeLookup)}}</div>
                  </div>
                </div>
              </div>

              <!-- Project Area -->
              <div class="optimisation-project-area no-sheet-selected text-muted font-italic" v-show="!hasSheetSelected">
                <div v-if="stationIsBreakout">
                  <h3>No sheets available for breakout</h3>
                </div>
                <div v-if="!stationIsBreakout">
                  <h3>No sheets in job</h3>
                  Drag a sheet to the sheet list or click on Add Sheet
                </div>
              </div>
              <div class="optimisation-project-area station-optimiser-panel-droppable-container" v-show="hasSheetSelected">
                <div class="station-optimisation-dropzone" v-show="showPanelDrop">Drop panel here to include in sheet</div>

                <div class="optimisation-editor-area">
                  <OptimiserSheetEditor
                    ref="sheetEditor"
                    :productionStage="productionStage"
                    :productionStation="productionStation"
                    :optimiserSheetId="currentSheetId"
                    @sheetAllocScanned="onScannedBarcode($event)"
                    @sheetUpdated="onSheetUpdated"
                    @sheetRemoved="onSheetRemoved">

                    <template v-slot:tools>
                      <div class="optimisation-sheet-control-bar-space"> </div>

                      <div class="optimisation-sheet-control-scanner">
                        <div class="row no-gutters">
                          <div class="col-5">
                            <div class="station-scanner-entry" v-show="isSelectedSheetInCutting">
                              <FormInputText ref="panelBarcodeScan" v-model="scanOutBarcodeEntry" placeholder="Barcode" @enter="onScannedBarcode()" prependSlot>
                                <template v-slot:prepend>
                                  <i class="far fa-scanner" v-if="!scanOutSubmitting"></i>
                                  <Spinner v-if="scanOutSubmitting" />
                                </template>
                              </FormInputText>
                            </div>
                          </div>
                          <div class="col-2 pl-2">
                            <StationControlPanelButton class="btn-default" sizeClass="xl" iconStyle="fal" icon="fa-scanner-keyboard" @click="scanOptionsClicked">Scan Options</StationControlPanelButton>
                          </div>
                          <div class="col-1 pl-2" v-if="isSelectedSheetInCutting && scanOutShowResult">
                            <StationErrorAnimation :scale="0.40" v-if="scanOutResultType == 'error'" />
                            <StationSuccessAnimation :scale="0.40" v-if="scanOutResultType == 'success'" />
                          </div>
                          <div class="col-4" v-if="isSelectedSheetInCutting && scanOutShowResult">
                            <h1 class="optimisation-sheet-scan-message">{{scanOutResultMessage}}</h1>
                          </div>
                        </div>
                      </div>
                    </template>
                  </OptimiserSheetEditor>
                </div>
              </div>
            </div>

            <!-- Optimiser Status, Stock Levels & Allocations -->
            <OptimiserStockAllocationPage
              ref="optimiserStatusPage"
              v-show="cuttingTabIndex == 1"
              :productionStage="productionStage"
              :productionStation="productionStation"
              @warningCountUpdated="statusWarningCount = $event"
            />

            <!-- Panel Scanning -->
            <CuttingPanelScanPage
              ref="cuttingPanelScanPage"
              v-show="cuttingTabIndex == 2"
              :productionStage="productionStage"
              :productionStation="productionStation"
            />

            <!-- Glass Sheet Consume -->
            <CuttingSheetScanPage
              ref="cuttingSheetScanPage"
              v-show="cuttingTabIndex == 3"
              :productionStage="productionStage"
              :productionStation="productionStation"
            />

            <!-- Cutting History -->
            <CuttingLogPage
              ref="cuttingLogPage"
              v-show="cuttingTabIndex == 4"
              :productionStage="productionStage"
              :productionStation="productionStation"
            />

            <!-- Cutting Batch -->
            <CuttingBatchPage
              ref="cuttingBatchPage"
              v-show="cuttingTabIndex == 5"
              @panelClick="batchPanelClicked"
              :showItemDrop="showBatchDrop"
              :productionStage="productionStage"
              :productionStation="productionStation"
            />

          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.station-optimisation-view {
  flex: 100%;
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items:stretch;
  overflow: hidden;
  position: relative;
}
.station-optimisation-tabpage {
  flex: 100%;
  width: 100%;
  height: 100%;
  display: flex;
  padding-top: 4px;
  flex-direction: column;
  align-items:stretch;
}
.station-optimisation-dropzone {
  box-sizing: border-box;
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  right: 0;
  width: 100%;
  height: 100%;
  font-size: 26px;
  background: rgba(32, 32, 32, .6);
  color: #FFFFFF;
  border: 3px dashed #808080;
  border-radius: 10px;
  z-index: 10;
  text-align: center;
  display: flex;
  justify-content: center;
  align-items: center;
}

.optimisation-top-container {
  position: relative;
  margin-bottom: 2px;
  height: 140px;
}

.optimisation-job-container {
  position: absolute;
  padding: 2px 0px 0px 0px;
  top: 0;
  left: 0;
  bottom: 0;
  width: 75%;
  overflow: hidden;
  white-space: nowrap;
}
.optimisation-job-container > .title {
  font-size: 16px;
  color: #A0A0A0;
  font-weight: bold;
}

.optimisation-job-list {
  position: relative;
  width: 100%;
  height: 100%;
  overflow-x: auto;
  overflow-y: hidden;
  white-space: nowrap;
}
.optimisation-job-list-loading {
  width: 110px;
  height: 110px;
  display:inline-block;
  line-height: 110px;
  text-align: center;
  vertical-align: middle;
}
.optimisation-editor-info {
  position: absolute;
  right: 0;
  top: 0;
  bottom: 4px;
  width: 25%;
  padding-left: 6px;
  border-left: 4px solid #404040;
  color: #c7c7c7;
}

.optimisation-project-area {
  position: relative;
  display: flex;
  flex: 100%;
  flex-direction: row;
  align-items: stretch;
}
.no-sheet-selected {
  display: flex;
  text-align: center;
  width: 100%;
  flex-direction: column;
  justify-content: center;
  align-items: center;
}
.no-sheet-selected h3 {
  font-size: 2em;
}
.optimisation-editor-area {
  flex: 100%;
  position: relative;
}
.optimisation-stats-area {
  width: 250px;
}
.optimisation-sheet-control-bar {
  flex: 64px;
  padding-top: 6px;
  display: flex;
  flex-direction: row;
  align-items: stretch;
}

.optimisation-sheet-control-bar-space {
  width: 3%;  
}

.optimisation-sheet-control-scanner {
  flex: 1;
  display: flex;
  flex-direction: row;
  align-items: stretch;
}
.optimisation-sheet-control-scanner > .row {
  width: 100%;
}
.optimisation-sheet-scan-message {
  font-weight: 100;
  line-height: 1.2;
  font-size: 26px;
  margin: 0;
}


</style>

<script>
import _ from 'lodash'
import moment from 'moment'
import dragula from 'dragula'
import 'dragula/dist/dragula.css'

import formMixin from '@/mixins/form'
import Utils from '@/utils/utils'
import EventBus from '@/utils/eventBus'

import ProductionStationMixin from '@/apps/glass-production-station/src/mixins/productionStation'
import StationButtonGroup from '@/apps/glass-production-station/src/components/main/stationButtonGroup'
import StationButtonGroupButton from '@/apps/glass-production-station/src/components/main/stationButtonGroupButton'
import StationPanelScheduleList from '@/apps/glass-production-station/src/components/schedule/stationPanelScheduleList'
import StationOrderScheduleList from '@/apps/glass-production-station/src/components/schedule/stationOrderScheduleList'
import StationPerformanceGraph from '@/apps/glass-production-station/src/components/stationPerformanceGraph'
import StationControlPanelButton from '@/apps/glass-production-station/src/components/buttons/stationControlPanelButton'

import StationErrorAnimation from '@/apps/glass-production-station/src/components/animations/stationErrorAnimation'
import StationSuccessAnimation from '@/apps/glass-production-station/src/components/animations/stationSuccessAnimation'

import CuttingPanelScanPage from '@/apps/glass-production-station/src/components/station/cuttingPanelScanPage'
import CuttingSheetScanPage from '@/apps/glass-production-station/src/components/station/cuttingSheetScanPage'
import CuttingLogPage from '@/apps/glass-production-station/src/components/station/cuttingLogPage'
import CuttingBatchPage from '@/apps/glass-production-station/src/components/station/cuttingBatchPage'
import OptimiserStockAllocationPage from '@/apps/glass-production-station/src/components/station/optimiserStockAllocationPage'

import OptimiserPanelList from '@/apps/glass-production-station/src/components/station/optimiserPanelList'
import OptimiserSheetList from '@/apps/glass-production-station/src/components/station/optimiserSheetList'
import PreviewSheetDialog from '@/apps/glass-production-station/src/views/optimiser/previewSheetDialog'
import OptimiserSheetEditDialog from '@/apps/glass-production-station/src/views/optimiser/optimiserSheetEditDialog'
import OptimiserAutoAddPanelDialog from '@/apps/glass-production-station/src/views/optimiser/optimiserAutoAddPanelDialog'
import GlassOffcutStationDialog from '@/apps/glass-production-station/src/views/glassOffcutStationDialog'
import QueueOptimiserSheet from '@/apps/glass-production-station/src/components/station/queueOptimiserSheet'
import QueueOptimiserOptions from '@/apps/glass-production-station/src/components/station/queueOptimiserOptions'
import OptimiserSheetEditor from '@/apps/glass-production-station/src/components/station/optimiserSheetEditor'


import { ProductionStationTypes, ProductionPanelStatusTypes, OptimiserSheetStatusTypes, SheetAllocationTypes, StockFaceTypeLookup, StockGrainDirectionTypeLookup } from '@/store/types'

export default {
  name: 'CuttingOptimiserStationView',
  mixins: [
    formMixin,
    ProductionStationMixin
  ],
  components: {
    StationButtonGroup,
    StationButtonGroupButton,
    StationPanelScheduleList,
    StationOrderScheduleList,
    StationPerformanceGraph,
    OptimiserSheetList,
    OptimiserPanelList,
    QueueOptimiserSheet,
    QueueOptimiserOptions,
    OptimiserSheetEditor,
    StationControlPanelButton,
    CuttingPanelScanPage,
    CuttingSheetScanPage,
    CuttingLogPage,
    CuttingBatchPage,
    OptimiserStockAllocationPage,
    StationErrorAnimation,
    StationSuccessAnimation
  },
  props: {
    productionStage: Object,
    productionStation: Object
  },
  data() {
    return {
      scheduleTabIndex: 0,
      cuttingTabIndex: 0,
      showSheetDrop: false,
      showPanelDrop: false,
      showBatchDrop: false,

      scanOutBarcodeEntry: '',
      scanOutSubmitting: false,
      scanOutShowResult: false,
      scanOutResultType: '',
      scanOutResultMessage: '',

      sheetListLoading: false,
      currentSheetId: null,
      stationSheetList: [],
      statusWarningCount: 0
    }
  },
  computed: {
    stationIsBreakout() {
      return this.productionStation.type == ProductionStationTypes.cuttingBreakout
    },
    stationSheetCount() {
      return this.stationSheetList.length
    },
    stationSheetQueueEmpty() {
      return this.stationSheetList.length == 0
    },
    hasSheetSelected() {
      return this.currentSheetId != null
    },
    selectedSheet() {
      return this.stationSheetList.find(sheet => sheet.id == this.currentSheetId)
    },
    isSelectedSheetInCutting() {
      if(!this.selectedSheet)
        return false
      return this.selectedSheet.status == OptimiserSheetStatusTypes.cutting
    }
  },
  watch: {
    isSelectedSheetInCutting: function() {
      this.checkBarcodeEntryFocus()
    },
    currentSheetId: function() {
      this.checkBarcodeEntryFocus()

      this.scanOutShowResult = false
    }
  },
  created() {
    this.OptimiserSheetStatusTypes = OptimiserSheetStatusTypes
    this.StockFaceTypeLookup = StockFaceTypeLookup
    this.StockGrainDirectionTypeLookup = StockGrainDirectionTypeLookup
  },
  mounted() {
    this.requestStationSheetData()

    this.subscribeEndpoint(`/production/stations/${this.productionStation.id}`, () => {
      this.requestStationSheetData()
    })
    this.subscribeEndpoint(`/orders`, (data) => {
      this.onOrderUpdated(data.orderIdList || [])
    })

    // Setup Drag & Drop support for sheets & panels in the optimiser
    // Disabled for breakout station
    let stationView = this
    if(!this.stationIsBreakout) {
      this.$options.drake = dragula([this.$refs.scheduleLeftBar], {
        copy: true,
        removeOnSpill: true,
        revertOnSpill: true,
        slideFactorX: 5,
        slideFactorY: 5,
        isContainer: function(el) {
          if(el.classList.contains('station-optimiser-sheet-draggable-container') || el.classList.contains('station-optimiser-panel-draggable-container'))
            return true
          if(el.classList.contains('station-optimiser-sheet-droppable-container') || el.classList.contains('station-optimiser-panel-droppable-container'))
            return true
          if(el.classList.contains('station-batch-draggable-container') || el.classList.contains('station-batch-droppable-container'))
            return true

          return false
        },
        moves: function (el, source, handle, sibling) {
          return el.classList.contains('station-optimiser-sheet-draggable') || el.classList.contains('station-optimiser-panel-draggable') || el.classList.contains('station-batch-draggable');
        },
        accepts: function (el, target, source, sibling) {
          if(!el)
            return false
          
          if(el.dataset.sheetid && el.dataset.type == 'sheet') {
            if(target.classList.contains('station-optimiser-sheet-droppable-container'))
              return true
          }

          if(el.dataset.orderid && el.dataset.type == 'order') {
            if(target.classList.contains('station-batch-droppable-container'))
              return true
          }
            
          // If dragging a panel over a sheet, convert it into a dragging mode to insert directly into the sheet's node tree
          if(el.dataset.panelid && el.dataset.type == 'glass') {
            if(target.classList.contains('station-optimiser-panel-droppable-container')) {
              stationView.$options.drake.cancel(true)

              stationView.$refs.sheetEditor.panelDraggedToSheet({
                panelId: parseInt(el.dataset.panelid),
                stockId: parseInt(el.dataset.stockid),
                width: parseInt(el.dataset.width),
                height: parseInt(el.dataset.height),
                grainDirection: el.dataset.grain,
                stockFaceSideUpType: el.dataset.facesideup
              })
              return false
            }

            // Check dragging panels to batch area
            if(target.classList.contains('station-batch-droppable-container'))
              return true
          }

          return false
        }
      })
      this.$options.drake.on('out', (el, container, source) => {
        let isSheet = el.classList.contains('station-optimiser-sheet-draggable')
        let isPanel = el.classList.contains('station-optimiser-panel-draggable')
        let isBatchDraggable = el.classList.contains('station-batch-draggable')

        if(isSheet && container.classList.contains('station-optimiser-sheet-droppable-container'))
          stationView.showSheetDrop = false
        if(isPanel && container.classList.contains('station-optimiser-panel-droppable-container'))
          stationView.showPanelDrop = false
        if(isBatchDraggable && container.classList.contains('station-batch-droppable-container'))
          stationView.showBatchDrop = false
      })
      this.$options.drake.on('over', (el, container, source) => {
        let isSheet = el.classList.contains('station-optimiser-sheet-draggable')
        let isPanel = el.classList.contains('station-optimiser-panel-draggable')
        let isBatchDraggable = el.classList.contains('station-batch-draggable')

        if(isSheet && container.classList.contains('station-optimiser-sheet-droppable-container'))
          stationView.showSheetDrop = true
        if(isPanel && container.classList.contains('station-optimiser-panel-droppable-container'))
          stationView.showPanelDrop = true
        if(isBatchDraggable && container.classList.contains('station-batch-droppable-container'))
          stationView.showBatchDrop = true
      })
      this.$options.drake.on('drop', (el, container, source) => {
        this.$options.drake.cancel(true)

        // Handle dropping the sheet / panel / order
        if(el.dataset.sheetid && el.dataset.type == 'sheet') {
          if(container.classList.contains('station-optimiser-sheet-droppable-container')) {
            stationView.addOptimiserSheetToJobQueue(parseInt(el.dataset.sheetid))
          }
        }

        if(container.classList.contains('station-batch-droppable-container')) {
          if(el.dataset.panelid && el.dataset.type == 'glass')
            stationView.addPanelToBatchPage(parseInt(el.dataset.panelid))
          if(el.dataset.orderid && el.dataset.type == 'order')
            stationView.addOrderToBatchPage(parseInt(el.dataset.orderid))
        }
      })
    }
  },
  beforeDestroy() {
    this.unsubscribeEndpoint(`/production/stations/${this.productionStation.id}`)
    this.unsubscribeEndpoint(`/orders`)
  },
  methods: {
    selectOptimiserTab() {
      this.cuttingTabIndex = 0

      this.$nextTick(() => {
        this.$refs.sheetEditor.render()

        // Clear scan results
        this.scanOutShowResult = false
        this.scanOutSubmitting = false
      })
    },
    selectOptimiserStatusTab() {
      this.cuttingTabIndex = 1
      this.$refs.optimiserStatusPage.pageSelected()
    },
    selectManualCuttingTab() {
      this.cuttingTabIndex = 2
      this.$refs.cuttingPanelScanPage.pageSelected()
    },
    selectGlassSheetTab() {
      this.cuttingTabIndex = 3
      this.$refs.cuttingSheetScanPage.pageSelected()
    },
    selectManualCuttingLogTab() {
      this.cuttingTabIndex = 4
      this.$refs.cuttingLogPage.pageSelected()
    },
    selectBatchTab() {
      this.cuttingTabIndex = 5
      this.$refs.cuttingBatchPage.pageSelected()
    },

    panelClicked(info) {
      this.$emit('panelClick', { panel: info.panel, target: this.$refs.scheduleLeftBar, forceElement: true })
    },
    batchPanelClicked(info) {
      this.$emit('panelClick', { panel: info.panel, target: info.target, forceElement: true })
    },
    orderClicked(info) {
      this.$emit('orderClick', { order: info.order, target: this.$refs.scheduleLeftBar, forceElement: true })
    },
    sheetClicked(info) {
      this.displayModal(PreviewSheetDialog, { 
        optimiserSheetId: info.sheet.id
      })
      .then(() => {
      })
    },
    requestStationSheetData() {
      return new Promise((resolve, reject) => {

        this.sheetListLoading = true

        // Get list of sheets at the station
        this.$apiService.glassProductionStationService.getSheetListAtStation(this.productionStation.id)
        .then(response => {
          this.stationSheetList = response.data

          // Clear editor if no sheets in the station list
          if(this.stationSheetList.length == 0)
            this.currentSheetId = null

          // Select 1st sheet if none selected
          if(this.currentSheetId == null && this.stationSheetList.length > 0)
            this.selectSheet(this.stationSheetList[0].id)

          // Update editor
          this.$nextTick(() => {
            if(this.$refs.sheetEditor)
              this.$refs.sheetEditor.render()
          })

          this.checkBarcodeEntryFocus()

          return resolve()
        })
        .catch(err => {
          console.error(err)
          this.displayNetworkError(err, 'Error accessing data')

          return resolve()
        })
        .finally(() => {
          this.sheetListLoading = false
        })
      })
    },
    addOptimiserSheetToJobQueue(optimiserSheetId) {
      this.sheetListLoading = true

      this.$apiService.glassProductionStationService.addSheetToStation(this.productionStation.id, { optimiserSheetId: optimiserSheetId })
      .then(response => {
        this.requestStationSheetData()
      })
      .catch(err => {
        console.error(err)
        this.sheetListLoading = false
        this.displayNetworkError(err, 'Error adding sheet')
      })
    },
    addPanelsFromScheduleList(schedulePanels) {
      // Filter out panels on hold or unavailable
      let filteredPanels = schedulePanels.filter(panel => {
        if(panel.isOnHold)
          return false
        if(panel.optimiserSheetId == null)
          return true
        if(panel.status != ProductionPanelStatusTypes.pending && panel.status != ProductionPanelStatusTypes.production)
          return false
        return (panel.optimiserSheetStatus == OptimiserSheetStatusTypes.open || panel.optimiserSheetStatus == OptimiserSheetStatusTypes.processing || panel.optimiserSheetStatus == OptimiserSheetStatusTypes.cancelled)
      })

      if(filteredPanels.length == 0) {
        this.errorMessage('No panels available', 'No panels available for adding to sheets')
        return
      }
      if(!this.hasSheetSelected) {
        this.errorMessage('No sheet selected', 'Must select a sheet to add panels to')
        return
      }

      // Request options
      this.displayModal(OptimiserAutoAddPanelDialog, {
        stockId: this.selectedSheet.stockId,
        panelCount: filteredPanels.length
      })
      .then(addPanelInfo => {
        if(addPanelInfo) {
          let updateData = {
            panels: filteredPanels.map(panel => _.pick(panel, ['id'])),
            createNewSheets: addPanelInfo.createNewSheets,
            glassLevelId: addPanelInfo.glsasLevelId,
            trimTop: addPanelInfo.trimTop,
            trimRight: addPanelInfo.trimRight,
            trimBottom: addPanelInfo.trimBottom,
            trimLeft: addPanelInfo.trimLeft
          }

          this.$refs.sheetEditor.setUpdatingServer(true)
          this.$apiService.glassProductionStationService.addPanelsToOptimiserSheet(this.productionStation.id, this.currentSheetId, updateData)
          .then(response => {

            this.$refs.sheetEditor.reloadOptimiserSheet()
            this.requestStationSheetData()
          })
          .catch(err => {
            console.error(err)
            this.displayNetworkError(err, 'Error adding panels')
          })
          .finally(() => {
            this.$refs.sheetEditor.setUpdatingServer(false)
          })
        }
      })
    },
    
    selectSheet(optimiserSheetId) {
      this.currentSheetId = optimiserSheetId
    },
    createSheet() {
      this.displayModal(OptimiserSheetEditDialog, {
        isCreateMode: true,
        productionStationId: this.productionStation.id,
        editing: true
      }, this.optimiserSheet)
      .then(sheetList => {
        if(sheetList) {
          this.requestStationSheetData()
          .then(() => {
            // Select the first newly created sheet
            if(_.isArray(sheetList) && sheetList.length > 0)
              this.selectSheet(sheetList[0].id)
          })
        }
      })      
    },
    optimiseQueue() {
      let stationView = this
      this.displayProcessingDialog({
        title: 'Optimising',
        message: 'Optimising all editing sheets in the queue',
        icon: 'fa-magic',
        onBeforeOpen: async (instance) => {
          try {
            await stationView.$apiService.glassProductionStationService.optimiseSheetQueue(stationView.productionStation.id)

            // Reset selected sheet & update full sheet list
            this.selectSheet(null)
            
            stationView.requestStationSheetData()
            .then(() => {
            })

            instance.hide()

          } catch(err) {
            instance.hide()
            console.error(err)
            stationView.displayNetworkError(err, 'Unable to optimise queue')
          }
        }
      })
    },
    clearQueue() {
      this.confirmDeleteMessage('Remove Editing Sheets?', '', { html: 'Are you sure you want to remove all sheets in the editing queue?<br />Any changes to the layout(s) will be lost!' })
      .then((success) => {

        if(success) {
          let stationView = this
          this.displayProcessingDialog({
            title: 'Removing Queue',
            message: 'Remove all editing sheets in the queue',
            icon: 'fa-trash',
            onBeforeOpen: async (instance) => {
              try {
                await stationView.$apiService.glassProductionStationService.removeSheetQueue(stationView.productionStation.id)

                stationView.requestStationSheetData()
                .then(() => {
                })

                instance.hide()

              } catch(err) {
                instance.hide()
                console.error(err)
                stationView.displayNetworkError(err, 'Unable to remove queue')
              }
            }
          })
        }
      })
    },

    
    onScannedBarcode(barcodeOverride = null) {
      if(barcodeOverride)
        this.scanOutBarcodeEntry = barcodeOverride

      if(this.scanOutBarcodeEntry.trim().length == '') {
        this.scanOutBarcodeEntry = ''
        return
      }

      this.scanOutSubmitting = true
      this.scanOutShowResult = false

      // Run the scan function for the optimiser sheet
      // Could be offcut or panel
      this.$apiService.glassProductionStationService.runSheetOptimiserBarcodeScan(this.productionStation.id, this.selectedSheet.id, { barcode: this.scanOutBarcodeEntry })
      .then(response => {
        this.scanOutSubmitting = false
        let scanResults = response.data
        var showingOffcutDialog = false

        // If we scanned an offcut, present the options to fill in offcut details
        if(scanResults.type == SheetAllocationTypes.offcut) {
          showingOffcutDialog = true

          this.displayModal(GlassOffcutStationDialog, {
            id: scanResults.glassLevelId,
            productionStageId: this.productionStage.id,
            productionStationId: this.productionStation.id
          })
          .then(() => {
          })
        }

        // Show success result for panels
        // Offcuts will get dialog for info
        if(scanResults.type == SheetAllocationTypes.panel) {
          this.$nextTick(() => {
            this.scanOutShowResult = true
            this.scanOutResultType = 'success'
            this.scanOutResultMessage = 'Panel Scanned Out'
          })
        }

        // Update sheet list
        this.requestStationSheetData()
        this.$refs.sheetEditor.reloadOptimiserSheet()

        this.scanOutBarcodeEntry = ''
        if(!showingOffcutDialog)
          this.$refs.panelBarcodeScan.focus()
      })
      .catch(err => {
        this.scanOutSubmitting = false
        let errorInfo = this.processFormResponse(err)
        this.scanOutBarcodeEntry = ''

        // Check if offcut info set
        // If so, bring up offcut dialog so we can manage the offcut
        if(errorInfo.errorData && errorInfo.errorData.isOffcut && errorInfo.errorData.glassLevelId) {
          this.displayModal(GlassOffcutStationDialog, {
            id: errorInfo.errorData.glassLevelId,
            productionStageId: this.productionStage.id,
            productionStationId: this.productionStation.id
          })
          .then(() => {
          })

          return
        }

        // Show errors as a popup as they will contain more details
        this.displayPopupAlert('error', 'Scanning Error!', errorInfo.errorMessage, { hasTimeout: true })

        this.$refs.panelBarcodeScan.focus()
      })
    },
    onSheetUpdated(sheet) {
      // Update sheet thumbnail in station sheet list
      let stationSheetIndex = this.stationSheetList.findIndex(stationSheet => stationSheet.id == sheet.id)
      if(stationSheetIndex != -1) {
        this.$set(this.stationSheetList, stationSheetIndex, sheet)
      }

      if(this.$refs.optimiserPanelList)
        this.$refs.optimiserPanelList.reloadPanelScheduleList()
    },
    onSheetRemoved(sheet) {
      this.stationSheetList = this.stationSheetList.filter(stationSheet => stationSheet.id != sheet.id)
      if(this.currentSheetId == sheet.id) {
        this.currentSheetId = null
        if(this.stationSheetList.length > 0)
          this.currentSheetId = this.stationSheetList[0].id
      }
    },
    checkBarcodeEntryFocus() {
      // Focus barcode entry if in cutting
      if(this.isSelectedSheetInCutting) {
        let stationView = this

        // Uses timeout as nextTick() wasn't working for somereason
        setTimeout(() => {
          if(stationView.$refs.panelBarcodeScan) {
            stationView.$refs.panelBarcodeScan.select()
            stationView.$refs.panelBarcodeScan.focus()
          }
        }, 100)
      }
    },
    onOrderUpdated(orderIdList) {
      this.$refs.sheetEditor.onOrderUpdated(orderIdList)
    },
    addPanelToBatchPage(panelId) {
      this.$refs.cuttingBatchPage.addPanelToBatchPage(panelId);
    },
    addOrderToBatchPage(orderId) {
      this.$refs.cuttingBatchPage.addOrderToBatchPage(orderId);
    },
    scanOptionsClicked() {
      this.displayStationScanOptions(this.productionStation.id)
    }
  }
  
}
</script>